import { useState, useEffect, useRef } from "react";
import type { NewVehiclePayload } from "../api";

export type VehicleSuggestion = {
  id: string;
  plate: string;
  make: string;
  model: string;
  color: string;
};

function normalizePlate(plate: string): string {
  return plate.trim().toUpperCase();
}

type AddVehicleFormProps = {
  existingVehicles: VehicleSuggestion[];
  onSubmitVehicle: (payload: NewVehiclePayload) => Promise<void>;
  onCancel: () => void;
};

const EMPTY_FORM = {
  licensePlate: "",
  make: "",
  model: "",
  color: "",
};

export default function AddVehicleForm({
  existingVehicles,
  onSubmitVehicle,
  onCancel,
}: AddVehicleFormProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<VehicleSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function updateField<K extends keyof typeof EMPTY_FORM>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validateForm(values: { licensePlate: string; make: string; model: string; color?: string }): string {
    if (!values.licensePlate.trim()) return "License plate is required.";
    if (!values.make.trim()) return "Make is required.";
    if (!values.model.trim()) return "Model is required.";
    return "";
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      const normalizedQuery = normalizePlate(searchQuery);
      const query = normalizedQuery.toLowerCase();
      if (query.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      const matches = existingVehicles.filter((v) => {
        const normalizedPlate = normalizePlate(v.plate);
        return (
          normalizedPlate.includes(normalizedQuery) ||
          v.make.toLowerCase().includes(query) ||
          v.model.toLowerCase().includes(query)
        );
      });

      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, existingVehicles]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const payload: NewVehiclePayload = {
      licensePlate: form.licensePlate.trim().toUpperCase(),
      make: form.make.trim(),
      model: form.model.trim(),
      color: form.color.trim(),
    };

    const validationError = validateForm(payload);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmitVehicle(payload);
      setForm(EMPTY_FORM);
      setSearchQuery("");
    } catch (err: any) {
      setError(err?.message || "Unable to add vehicle. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function selectSuggestion(vehicle: VehicleSuggestion) {
    setForm({
      licensePlate: normalizePlate(vehicle.plate),
      make: vehicle.make.trim(),
      model: vehicle.model.trim(),
      color: vehicle.color.trim(),
    });
    setSearchQuery("");
    setShowSuggestions(false);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ position: "relative", marginBottom: "24px" }}>
        <label
          htmlFor="licensePlate"
          style={{
            display: "block",
            fontSize: "14px",
            color: "var(--ink)",
            marginBottom: "8px",
            fontWeight: 500,
            fontFamily: "var(--font-body)",
          }}
        >
          Search Vehicle (plate, make, or model)
        </label>
        <input
          ref={searchInputRef}
          id="licensePlate"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search or add new vehicle..."
          autoComplete="off"
          className="input-field"
        />
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "var(--white)",
              border: "1px solid var(--line)",
              borderRadius: "var(--radius-sm)",
              marginTop: "4px",
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "var(--shadow-panel)",
              zIndex: 1000,
            }}
          >
            {suggestions.map((vehicle) => (
              <div
                key={vehicle.id}
                onClick={() => selectSuggestion(vehicle)}
                style={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  borderBottom: "1px solid var(--line)",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--azzurro-50)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--white)";
                }}
              >
                <div style={{ fontSize: "15px", fontWeight: 500, color: "var(--ink)" }}>
                  {vehicle.plate}
                </div>
                <div style={{ fontSize: "13px", color: "var(--stone)", marginTop: "2px" }}>
                  {vehicle.make} {vehicle.model} — {vehicle.color || "No color"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: "24px" }}>
        <label
          htmlFor="licensePlateInput"
          style={{
            display: "block",
            fontSize: "14px",
            color: "var(--ink)",
            marginBottom: "8px",
            fontWeight: 500,
            fontFamily: "var(--font-body)",
          }}
        >
          License Plate <span style={{ color: "var(--danger)" }}>*</span>
        </label>
        <input
          id="licensePlateInput"
          type="text"
          value={form.licensePlate}
          onChange={(e) => updateField("licensePlate", e.target.value)}
          placeholder="ABC123"
          maxLength={10}
          className="input-field"
        />
      </div>

      <div style={{ marginBottom: "24px" }}>
        <label
          htmlFor="make"
          style={{
            display: "block",
            fontSize: "14px",
            color: "var(--ink)",
            marginBottom: "8px",
            fontWeight: 500,
            fontFamily: "var(--font-body)",
          }}
        >
          Make <span style={{ color: "var(--danger)" }}>*</span>
        </label>
        <input
          id="make"
          type="text"
          value={form.make}
          onChange={(e) => updateField("make", e.target.value)}
          placeholder="e.g., Toyota"
          className="input-field"
        />
      </div>

      <div style={{ marginBottom: "24px" }}>
        <label
          htmlFor="model"
          style={{
            display: "block",
            fontSize: "14px",
            color: "var(--ink)",
            marginBottom: "8px",
            fontWeight: 500,
            fontFamily: "var(--font-body)",
          }}
        >
          Model <span style={{ color: "var(--danger)" }}>*</span>
        </label>
        <input
          id="model"
          type="text"
          value={form.model}
          onChange={(e) => updateField("model", e.target.value)}
          placeholder="e.g., Camry"
          className="input-field"
        />
      </div>

      <div style={{ marginBottom: "24px" }}>
        <label
          htmlFor="color"
          style={{
            display: "block",
            fontSize: "14px",
            color: "var(--ink)",
            marginBottom: "8px",
            fontWeight: 500,
            fontFamily: "var(--font-body)",
          }}
        >
          Color
        </label>
        <input
          id="color"
          type="text"
          value={form.color}
          onChange={(e) => updateField("color", e.target.value)}
          placeholder="e.g., White"
          className="input-field"
        />
      </div>

      {error ? (
        <div
          style={{
            padding: "14px 16px",
            background: "rgba(220, 38, 38, 0.08)",
            color: "var(--danger)",
            borderRadius: "var(--radius-sm)",
            marginBottom: "24px",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: "12px" }}>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary"
          style={{
            background: isSubmitting ? "var(--stone)" : "var(--azzurro-700)",
            cursor: isSubmitting ? "not-allowed" : "pointer",
          }}
        >
          {isSubmitting ? "Adding..." : "Add Vehicle"}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="btn-secondary"
          style={{
            cursor: isSubmitting ? "not-allowed" : "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
